import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { GiftCardOrderCard } from '@garnish/commerce';
import { theme } from '@garnish/constants';
import { AnimatedViews, BodyText, DisplayText, useResponsive, VStack } from '@garnish/core';

import { useTrackEventEffect } from '@order/Telemetry';

import { GiftCardOrder, GiftCardOrdersEmpty } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardOrders = (props: GiftCardOrdersProps) => {
  const { currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  useTrackEventEffect({ name: 'gift.history_viewed' });

  // ─────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <View style={styles.container}>
        <BodyText style={styles.titleXS} sizeMatch={['16']}>
          {formatMessage(messages.title)}
        </BodyText>

        <GiftCardOrdersContent {...props} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <DisplayText sizeMatch={['32']}>{formatMessage(messages.title)}</DisplayText>

      <GiftCardOrdersContent {...props} />
    </View>
  );
};

const GiftCardOrdersContent = (props: GiftCardOrdersProps) => {
  const {
    isFetchingOrders,
    giftCardOrders,
    onOpenGiftCardsScreen,
    getConfirmationLink,
    onOrderPress,
  } = props;

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({ name: 'gift.history_viewed' });

  // ─────────────────────────────────────────────────────────────────────

  if (isFetchingOrders) {
    return <GiftCardOrdersLoading />;
  }

  if (giftCardOrders.length === 0) {
    return <GiftCardOrdersEmpty onPress={onOpenGiftCardsScreen} />;
  }

  return (
    <GiftCardOrdersReady
      giftCardOrders={giftCardOrders}
      getConfirmationLink={getConfirmationLink}
      onOrderPress={onOrderPress}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const GiftCardOrdersReady = (
  props: Pick<GiftCardOrdersProps, 'giftCardOrders' | 'getConfirmationLink' | 'onOrderPress'>,
) => {
  const { giftCardOrders, getConfirmationLink, onOrderPress } = props;
  const { match } = useResponsive();

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({ name: 'gift.history_success' });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <VStack
      gap={theme.spacing['10']}
      hasDivider
      dividerColor={theme.colors.LIGHT}
      style={match([undefined, styles.ordersContainerSM])}
    >
      {giftCardOrders.map((order, index) => {
        const { id, purchasedAt, deliveryDetail, userGiftCards } = order;

        const mainGiftCard = userGiftCards?.[0];
        const { recipientName, balance } = mainGiftCard ?? {};

        return (
          <AnimatedViews.Reveal key={id} delay={getRevealDelay(index)}>
            <GiftCardOrder
              id={id}
              recipientName={recipientName}
              deliveryMethods={deliveryDetail.deliveryMethods}
              balance={balance}
              purchaseDate={purchasedAt}
              getConfirmationLink={getConfirmationLink}
              onPress={onOrderPress}
            />
          </AnimatedViews.Reveal>
        );
      })}
    </VStack>
  );
};

const GiftCardOrdersLoading = () => {
  const { match, currentBreakpoint } = useResponsive();

  const placeholderIndexes = [...Array(PLACEHOLDER_ORDERS).keys()];

  return (
    <VStack
      gap={theme.spacing['10']}
      hasDivider
      dividerColor={theme.colors.LIGHT}
      style={match([undefined, styles.ordersContainerSM])}
    >
      {placeholderIndexes.map((index) => (
        <AnimatedViews.Pulse key={index} index={index}>
          <GiftCardOrderCard.Container>
            {currentBreakpoint.isXS ? <View style={styles.iconPlaceholder} /> : null}

            <GiftCardOrderCard.ContentContainer style={styles.placeholderContent}>
              <View style={styles.datePlaceholder} />

              <View style={styles.idPlaceholder} />

              <View style={styles.recipientPlaceholder} />
            </GiftCardOrderCard.ContentContainer>

            <GiftCardOrderCard.ExtraDetailsContainer style={styles.placeholderExtraDetails}>
              <View style={styles.amountPlaceholder} />
            </GiftCardOrderCard.ExtraDetailsContainer>
          </GiftCardOrderCard.Container>
        </AnimatedViews.Pulse>
      ))}
    </VStack>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getRevealDelay(index: number) {
  return Math.min(index, MAX_STAGGERED_ORDERS) * ORDER_REVEAL_STAGGER;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_ORDERS = 3;

const ORDER_REVEAL_STAGGER = 65;
const MAX_STAGGERED_ORDERS = 4;

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Orders',
    description: 'Gift card orders | Title',
  },
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing['4'],
  },
  titleXS: {
    paddingBottom: theme.spacing['2'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
  },
  ordersContainerSM: {
    padding: theme.spacing['6'],
    borderRadius: theme.radius.large,
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
  },
  placeholderContent: {
    gap: theme.spacing['1'],
  },
  placeholderExtraDetails: {
    justifyContent: 'center',
  },
  iconPlaceholder: {
    alignSelf: 'center',
    width: 24,
    height: 24,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  datePlaceholder: {
    width: 148,
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  idPlaceholder: {
    width: 176,
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  recipientPlaceholder: {
    width: 152,
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  amountPlaceholder: {
    width: 64,
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardOrdersProps = {
  isFetchingOrders: boolean;
  giftCardOrders: ReadonlyArray<{
    id: string;
    purchasedAt: string;
    deliveryDetail: {
      deliveryMethods: ReadonlyArray<string>;
    };
    userGiftCards: ReadonlyArray<{
      id: string;
      balance: number;
      recipientEmail: string | null;
      recipientName: string;
    }>;
  }>;
  onOpenGiftCardsScreen: () => void;
  getConfirmationLink: (giftCardOrderId: string) => string;
  onOrderPress?: (giftCardOrderId: string) => void;
};
